// Cache for converted markdown to prevent unnecessary conversions
const markdownCache = new Map<string, string>();

/**
 * Converts markdown text to HTML with proper styling for Flow summaries
 * @param markdown The markdown text to convert
 * @returns HTML string with proper styling
 */
export function convertMarkdownToHTML(markdown: string): string {
  if (!markdown) {
    return '';
  }
  
  // Check cache first
  const cacheKey = markdown;
  if (markdownCache.has(cacheKey)) {
    return markdownCache.get(cacheKey) || '';
  }
  
  console.log('Converting markdown to HTML, input length:', markdown.length);
  
  try {
    // Convert headings with even tighter spacing and proper alignment
    let html = markdown
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-3 mb-1 text-gray-900 pl-0 text-left !text-left">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mt-4 mb-1 text-gray-900 pl-0 text-left !text-left">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-2 mb-2 text-gray-900 pl-0 text-left !text-left">$1</h1>');
    
    // Convert bold and italics
    html = html
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold text-gray-900">$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em class="italic text-gray-900">$1</em>');
    
    // Convert bullet and numbered lists with proper indentation
    // First handle numbered lists
    html = html.replace(/^(\s*)(\d+)\.\s+(.*$)/gim, function(match, indent, number, content) {
      const indentClass = indent.length > 0 ? "ml-4 " : "";
      return `<li class="${indentClass}mb-0 text-gray-900" data-list-type="ordered">${content}</li>`;
    });
    
    // Then handle bullet lists
    html = html.replace(/^(\s*)- (.*$)/gim, function(match, indent, content) {
      // Keep indentation for sub-bullets, but align top-level bullets with headings
      const indentClass = indent.length > 0 ? "ml-4 " : "";
      return `<li class="${indentClass}mb-0 text-gray-900">${content}</li>`;
    });
    
    // Ensure lists are properly wrapped in <ul> tags without breaking them
    let listStarted = false;
    let listType = '';
    let lines = html.split('\n');
    let result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isOrderedListItem = line.match(/<li[^>]*data-list-type="ordered"/i);
      const isUnorderedListItem = line.includes('<li') && !isOrderedListItem;
      
      // Handle list items
      if (isOrderedListItem || isUnorderedListItem) {
        const currentListType = isOrderedListItem ? 'ol' : 'ul';
        
        // If we haven't started a list yet, or if we're switching list types, start a new list
        if (!listStarted) {
          if (currentListType === 'ol') {
            result.push('<ol class="list-decimal pl-5 ml-0 my-1">');
          } else {
            result.push('<ul class="list-disc pl-5 ml-0 my-1">');
          }
          listStarted = true;
          listType = currentListType;
        } else if (listType !== currentListType) {
          // Close current list and start a new one of different type
          result.push(`</${listType}>`);
          if (currentListType === 'ol') {
            result.push('<ol class="list-decimal pl-5 ml-0 my-1">');
          } else {
            result.push('<ul class="list-disc pl-5 ml-0 my-1">');
          }
          listType = currentListType;
        }
        
        result.push(line);
      } 
      // If this is not a list item but we are in a list
      else if (listStarted) {
        // Only close the list if the current line isn't a blank line
        // This keeps lists together even with empty lines between items
        if (line.trim() !== '' && !line.includes(`</${listType}>`) && !line.includes('<ul') && !line.includes('<ol')) {
          result.push(`</${listType}>`);
          listStarted = false;
          result.push(line);
        } else {
          // Just add blank lines or list-closing tags without closing our list context
          result.push(line);
        }
      } 
      // Otherwise just add the line
      else {
        result.push(line);
      }
    }
    
    // Close any open list at the end
    if (listStarted) {
      result.push(`</${listType}>`);
    }
    
    html = result.join('\n');
    
    // Proper paragraph handling with minimum spacing - only use double newlines for paragraphs
    html = html
      .replace(/\n\n/gim, '</p><p class="my-1 text-gray-900">');
    // Don't convert single newlines to <br/> tags to avoid extra line breaks
    
    // Wrap in paragraph if not already wrapped
    if (!html.startsWith('<h1') && !html.startsWith('<h2') && !html.startsWith('<h3') && !html.startsWith('<p')) {
      html = '<p class="my-1 text-gray-900">' + html + '</p>';
    }
    
    console.log('Markdown conversion successful, output length:', html.length);
    
    // Cache the result
    markdownCache.set(cacheKey, html);
    
    return html;
  } catch (error) {
    console.error('Error converting markdown to HTML:', error);
    // Fallback to simple conversion if there's an error
    const fallbackHtml = `<div class="bg-white p-4 rounded shadow text-gray-900">
      <h2 class="text-xl font-bold mb-4">Flow Summary</h2>
      <pre class="whitespace-pre-wrap text-gray-900">${markdown.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
    </div>`;
    
    // Cache the fallback result
    markdownCache.set(cacheKey, fallbackHtml);
    
    return fallbackHtml;
  }
}